import React from 'react';
import { useNavigate } from 'react-router';
import {
  Button,
  Card,
  CardFooter,
  CardSection,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  Image,
  LargeHeader,
  Paragraph,
  PluralizeUtil,
} from '@axiom/ui';

import type { TalentSearchActiveQueryFilters } from '../../schema/talent-search-active-query-filters';
import SeeMoreIcon from '../../public/images/see-more-people.png';
import { useTalentSearchCandidateStatsData } from '../../hooks/useTalentSearchCandidateStatsData';
import { WindowUtil } from '../../utils/window-util';
import { CandidateApi } from '../../api/candidate-api';

interface DataStrings {
  name: string;
  title: string;
  subtitle: string;
  body: string | React.JSX.Element;
}

export interface TalentSearchSeeMoreCardProps {
  activeFilters: TalentSearchActiveQueryFilters;
  handleReachOutClick: () => void;
}

const getStrings = (count: number): DataStrings => {
  const pluralizedProfile = PluralizeUtil({ text: 'profile', count });

  return {
    name: '',
    title:
      count > 0 ? 'Looking for more talent like this?' : 'Want to see more?',
    subtitle: '',
    body:
      count > 0
        ? `We have ${count} more ${pluralizedProfile} like these for Axiom talent
          who are available but aren’t shown here yet. Please reach out if you’d
          like to find out more about them.`
        : 'To see more profiles, try updating or clearing your filters. If you’d like help finding more talent like this, including talent not shown on this page, please reach out to Axiom.',
  };
};

export const TalentSearchSeeMoreCard = ({
  activeFilters,
  handleReachOutClick,
}: TalentSearchSeeMoreCardProps) => {
  const navigate = useNavigate();
  const stats = useTalentSearchCandidateStatsData({ activeFilters });
  const { title, body } = getStrings(stats.endOfTalentCount ?? 0);
  return (
    <Card name="TALENT_SEARCH_SEE_MORE">
      <CardSection divider>
        <Grid>
          <GridRow>
            <GridColumn largeScreenWidth={2} tabletWidth={3} mobileWidth={12}>
              <Image
                alt="see-more-people"
                name="SEE_MORE_ICON"
                src={SeeMoreIcon}
                width="100px"
              />
            </GridColumn>
            <GridColumn largeScreenWidth={10} tabletWidth={9} mobileWidth={12}>
              <Gutter top="24px" only="mobile" />
              <Gutter bottom="16px">
                <LargeHeader name="TALENT_SEARCH_SEE_MORE_TITLE">
                  {title}
                </LargeHeader>
              </Gutter>
              <Paragraph name="TALENT_SEARCH_SEE_MORE_BODY">{body}</Paragraph>
            </GridColumn>
          </GridRow>
        </Grid>
      </CardSection>
      <CardFooter>
        <Button
          variation="outline"
          name="SEE_MORE_CLEAR_FILTERS"
          onClick={async () => {
            void navigate('/browse-talent/page/1');
            WindowUtil.setUrlQuery({}, true);
            await CandidateApi.refreshCandidateSearchResults();
          }}
        >
          Clear Filters
        </Button>
        <Button
          name="SEE_MORE_REACH_OUT"
          onClick={() => {
            handleReachOutClick();
          }}
        >
          Reach Out
        </Button>
      </CardFooter>
    </Card>
  );
};
